export type Cadence = 'one_time' | 'weekly' | 'monthly';

export type CommitmentStatus =
  | 'drafting'
  | 'pending_manager'
  | 'active'
  | 'paused'
  | 'pending_cancellation'
  | 'cancelled'
  | 'terminated'
  | 'completed';

export type CancellationDecisionType =
  | 'reject'
  | 'approve_change'
  | 'approve_termination';

/** Taxonomy of admin-cancel categories. Drives the monthly CNBV
 *  "cancelaciones por causa" report on the backend — keep this list
 *  in lockstep with `investments.admin_cancel_category` and the
 *  `_ADMIN_CANCEL_CATEGORIES` set in commitments service. */
export type AdminCancelCategory =
  | 'fraud'
  | 'regulatory_action'
  | 'duplicate'
  | 'data_error'
  | 'client_deceased'
  | 'client_incapacitated'
  | 'legal_dispute'
  | 'kyc_failed_revalidation'
  | 'other';

/** Categories where the backend REQUIRES `external_reference` (folio
 *  CNBV / oficio judicial / compliance ticket). The UI uses this set
 *  to flip the field to "required" and short-circuit the round-trip
 *  to a 400. */
export const ADMIN_CANCEL_CATEGORIES_REQUIRING_REFERENCE: ReadonlySet<AdminCancelCategory> =
  new Set(['regulatory_action', 'legal_dispute']);

export type AdminCancelSource =
  | 'manager_manual'
  | 'automated_job'
  | 'compliance_ticket';

export interface CancellationDecisionBody {
  decision_type: CancellationDecisionType;
  manager_notes: string;
  /** approve_change requires both renegotiation_assignee_id + account_agent_id */
  renegotiation_assignee_id?: string | null;
  account_agent_id?: string | null;
  /** approve_termination requires scheduled_return_date (ISO YYYY-MM-DD) */
  scheduled_return_date?: string | null;
}

export interface CancellationDecisionResult {
  decision: CancellationDecisionType | 'rejected';
  follow_up_case_id?: string;
  renegotiation_assignee_id?: string;
  account_agent_id?: string;
  reverted_to?: string;
  // Termination-only fields:
  balance_at_close?: string;
  retention_amount?: string;
  return_amount_scheduled?: string;
  scheduled_return_date?: string;
  deposits_auto_rejected?: number;
}

export interface CommitmentConfig {
  id: string;
  product_id: string;
  product_name: string;
  version: number;
  effective_from: string;
  effective_to: string | null;
  allowed_cadences: Cadence[];
  min_amount: string;
  max_amount: string | null;
  default_day_of_month: number | null;
  default_day_of_week: number | null;
  grace_days: number;
  reminder_lead_days: number;
  /** Fraction (0..1, exclusive on the upper end) — "0.05" means 5%. */
  early_termination_retention_pct: string;
  contract_title: string;
  contract_body: string;
  notes: string | null;
  created_at: string;
}

/**
 * Drafter-facing projection of the active product commitment config.
 * Returned by GET /commitments/products/{id}/active-policy — available
 * to agents / managers / super_admin (clients get 403). Does NOT
 * carry contract body, version metadata, audit IDs, or operator
 * notes; those live on the super_admin-only CommitmentConfig.
 */
export interface ActiveCommitmentPolicy {
  product_id: string;
  product_name: string;
  version: number;
  allowed_cadences: Cadence[];
  min_amount: string;
  max_amount: string | null;
  default_day_of_month: number | null;
  default_day_of_week: number | null;
  grace_days: number;
  reminder_lead_days: number;
}

export interface PublishConfigBody {
  allowed_cadences: Cadence[];
  min_amount: string;
  max_amount: string | null;
  default_day_of_month: number | null;
  default_day_of_week: number | null;
  grace_days: number;
  reminder_lead_days: number;
  early_termination_retention_pct: string;
  contract_title: string;
  contract_body: string;
  notes?: string | null;
}

export interface DraftCommitmentBody {
  user_id: string;
  product_id: string;
  cadence: Cadence;
  amount: string;
  start_date: string;
  end_date?: string | null;
  day_of_month?: number | null;
  day_of_week?: number | null;
  notes?: string | null;
}

export interface Commitment {
  id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  product_id: string;
  product_name: string;
  config_id: string;
  config_version: number;
  contract_case_id: string;
  contract_state_slug: string;
  cadence: Cadence;
  amount: string;
  currency_code: string;
  start_date: string;
  end_date: string | null;
  day_of_month: number | null;
  day_of_week: number | null;
  status: CommitmentStatus;
  cancellation_reason: string | null;
  cancelled_at: string | null;
  notes: string | null;
  created_at: string;
  version: number;
  /** Engagement metric: number of fulfilled expected_deposits for this
   *  commitment so far. Surfaced on MyCommitmentsCard as a soft
   *  consistency cue — never a streak / loss-aversion device. */
  fulfilled_count: number;
  /** Total scheduled expected_deposits for bounded commitments
   *  (end_date set). 0 for open-ended; the UI renders count-only in
   *  that case (no progress bar implying a finish line). */
  expected_count: number;
}

export interface DepositLimit {
  id: string;
  user_id: string;
  product_id: string;
  product_name: string;
  monthly_cap: string;
  effective_from: string;
  effective_to: string | null;
  reason: string | null;
}

export interface FollowupBrief {
  case_id: string;
  client: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    kyc_completed_at: string | null;
  };
  overdue_items: {
    expected_deposit_id: string;
    product_name: string;
    due_date: string;
    expected: string;
    fulfilled_so_far: string;
    gap: string;
    currency: string;
    days_overdue: number;
    status: string;
  }[];
  active_commitments: {
    commitment_id: string;
    product_name: string;
    cadence: Cadence;
    amount: string;
    currency: string;
    start_date: string;
    end_date: string | null;
    day_of_month: number | null;
    day_of_week: number | null;
    status: CommitmentStatus;
    config_version: number;
  }[];
  deposit_history_6m: {
    total_count: number;
    fulfilled_count: number;
    partial_count: number;
    missed_count: number;
    fulfillment_rate_pct: number;
    last_successful_deposit_at: string | null;
    trend: 'improving' | 'stable' | 'declining';
  };
  monthly_cap_status: {
    product_id: string;
    product_name: string;
    cap: string;
    used_this_month: string;
    remaining: string;
  }[];
  suggested_talking_points: string[];
  previous_followup_summary: {
    case_id: string;
    completed_at: string | null;
    resolution_notes: string | null;
  } | null;
  /** Present when the case was opened by an approved cancellation.
   *  `kind === 'termination'` triggers the red callout in the brief
   *  panel; `kind === 'change_of_terms'` shows a neutral block. */
  cancellation_context: {
    kind: 'change_of_terms' | 'termination';
    reason: string;
    commitment_id: string;
    product_id: string;
    cancellation_reason: string | null;
    requested_by_agent_id: string | null;
    manager_notes: string;
    approved_by_manager_id: string;
    termination?: {
      retention_pct: string;
      balance_at_close: string;
      retention_amount: string;
      return_amount_scheduled: string;
      scheduled_return_date: string;
      currency_code: string;
      deposits_auto_rejected: number;
    };
  } | null;

  // Adjacent signals added 2026-05-11. All nullable / optional so old
  // briefs that pre-date the extension don't break the type.
  kyc_status?: {
    valid_until: string;
    days_to_expiry: number;
    is_expired: boolean;
    revalidation_required: boolean;
  } | null;
  open_cases?: {
    case_id: string;
    workflow_slug: string;
    workflow_label: string;
    status: string;
    current_state_slug: string;
    assigned_to: string | null;
    assigned_to_name: string | null;
    created_at: string;
    is_spawned: boolean;
  }[] | null;
  pending_amendments?: {
    signed_document_id: string;
    contract_number: string;
    status: 'superseded' | 'revoked' | 'expired';
    signed_at: string | null;
    expires_at: string | null;
    template_title: string;
    template_version: number;
  }[] | null;
  last_contact?: {
    interaction_id: string;
    at: string;
    kind: string;
    outcome: string;
    by_user_name: string;
  } | null;
  // Present for manager and super_admin only; an agent's brief never carries the key.
  compliance_flags?: {
    kind: string;
    severity: 'info' | 'warning' | 'critical';
    message: string;
  }[] | null;
}
